import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Play, RotateCcw, Undo2, Eraser, CheckCircle2, ArrowRight, Star, Eye } from "lucide-react";
import { TracingCanvas } from "@/components/tracing/TracingCanvas";
import { analyzeStrokes, feedbackForScore, TOLERANCE_MODES } from "@/lib/scoring";
import { traceableFromConfig } from "@/data/printGeometry";
import { speak, cancelSpeech } from "@/lib/speak";

const STAGES = [
  { key: "watch", title: "Watch", hint: "Watch how it's made, then tap I'm ready." },
  { key: "trace", title: "Trace it", guide: 0.4, threshold: 70, hint: "Start at the dot and follow the line." },
  { key: "write", title: "Write it", guide: 0.15, threshold: 75, hint: "The guide is faint now — write over it carefully." },
];

// Universal handwriting engine: Watch → Trace → Write, scored by the cursive engine.
export default function TraceEngine({ activity, onComplete, autoplay = false }) {
  const cfg = activity.config || {};
  const traceable = useMemo(() => traceableFromConfig(cfg), [cfg.kind, cfg.text, cfg.style]); // eslint-disable-line react-hooks/exhaustive-deps
  const canvasRef = useRef(null);
  const animatorRef = useRef(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [strokeCount, setStrokeCount] = useState(0);
  const [result, setResult] = useState(null);
  const [scores, setScores] = useState([]);
  const stage = STAGES[stageIdx];
  const done = stageIdx >= STAGES.length;

  useEffect(() => {
    setStageIdx(0); setStrokeCount(0); setResult(null); setScores([]);
    if (autoplay || cfg.speak !== false) speak(activity.prompt || stage.hint);
    const t = setTimeout(() => animatorRef.current?.play(), 700);
    return () => { clearTimeout(t); cancelSpeech(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id]);

  if (!traceable) return <p className="text-center font-body text-[#94A3B8]" data-testid="trace-invalid">This tracing activity isn't set up yet.</p>;

  const label = cfg.kind === "stroke" || cfg.kind === "shape" ? (cfg.text || "").replace(/_/g, " ") : cfg.text;

  const goTo = (i) => { canvasRef.current?.clear(); setStrokeCount(0); setResult(null); animatorRef.current?.reset(); setStageIdx(i); if (i < STAGES.length) speak(STAGES[i].hint); };

  const check = () => {
    const strokes = canvasRef.current?.getStrokes() || [];
    const res = analyzeStrokes(strokes, traceable, { tolerance: TOLERANCE_MODES.standard });
    const fb = feedbackForScore(res.score);
    setResult({ ...res, ...fb, pass: res.score >= stage.threshold });
    speak(fb.title);
  };

  const next = () => {
    const s = [...scores, result.score];
    setScores(s);
    if (stageIdx + 1 >= STAGES.length) { setStageIdx(STAGES.length); speak("Great job!"); }
    else goTo(stageIdx + 1);
  };

  const finalScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 100;

  return (
    <div className="text-center" data-testid="trace-engine">
      <div className="flex items-center justify-center gap-2">
        <h3 className="font-heading text-2xl font-bold text-[#1F2937]">{activity.prompt || `Trace ${label}`}</h3>
        <button onClick={() => speak(activity.prompt || stage?.hint)} data-testid="trace-speak" className="rounded-full p-2 text-[#0D9488] hover:bg-[#0D9488]/10"><Volume2 size={20} /></button>
      </div>
      <div className="mt-2 flex items-center justify-center gap-2" data-testid="trace-stages">
        {STAGES.map((s, i) => (
          <span key={s.key} className={`rounded-full px-3 py-1 text-xs font-bold ${i === stageIdx ? "bg-[#0D9488] text-white" : i < stageIdx || done ? "bg-[#10B981]/15 text-[#047857]" : "bg-[#F2ECE1] text-[#94A3B8]"}`}>{s.title}</span>
        ))}
      </div>

      {!done ? (
        <>
          <p className="mt-2 font-body text-sm text-[#6B7280]">{stage.hint}</p>
          <div className="mx-auto mt-4 max-w-xl rounded-3xl border-2 border-[#E5DECF] p-2">
            <TracingCanvas ref={canvasRef} letter={traceable} animatorRef={animatorRef}
              guideOpacity={stage.key === "watch" ? 0.5 : stage.guide} showStart showLetterGuide
              drawingEnabled={stage.key !== "watch" && !result?.pass}
              onStrokesChange={(s) => setStrokeCount(s.length)} />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {stage.key === "watch" ? (
              <>
                <Btn testId="trace-play" onClick={() => animatorRef.current?.replay()}><Play size={16} /> Play again</Btn>
                <Btn testId="trace-ready" primary onClick={() => goTo(1)}>I'm ready to trace <ArrowRight size={16} /></Btn>
              </>
            ) : (
              <>
                <Btn testId="trace-show" onClick={() => animatorRef.current?.replay()}><Eye size={16} /> Show me</Btn>
                <Btn testId="trace-undo" disabled={!strokeCount} onClick={() => { canvasRef.current?.undo(); setResult(null); }}><Undo2 size={16} /> Undo</Btn>
                <Btn testId="trace-clear" disabled={!strokeCount} onClick={() => { canvasRef.current?.clear(); setStrokeCount(0); setResult(null); }}><Eraser size={16} /> Clear</Btn>
                {!result?.pass && <Btn testId="trace-check" primary disabled={!strokeCount} onClick={check}><CheckCircle2 size={16} /> Check</Btn>}
              </>
            )}
          </div>
          {result && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mx-auto mt-4 max-w-md rounded-2xl border border-[#E5DECF] bg-white p-4" data-testid="trace-result">
              <div className="flex justify-center gap-1">{[0, 1, 2].map((i) => <Star key={i} size={22} className={i < result.stars ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#E5DECF]"} />)}</div>
              <div className="mt-1 font-heading text-lg font-bold text-[#1F2937]">{result.title}</div>
              <div className="text-xs text-[#6B7280]">Score {result.score}%</div>
              <div className="mt-3 flex justify-center gap-2">
                {result.pass
                  ? <Btn testId="trace-next" primary onClick={next}>{stageIdx + 1 >= STAGES.length ? "Finish" : "Next step"} <ArrowRight size={16} /></Btn>
                  : <Btn testId="trace-retry" onClick={() => { canvasRef.current?.clear(); setStrokeCount(0); setResult(null); }}><RotateCcw size={16} /> Try again</Btn>}
              </div>
            </motion.div>
          )}
        </>
      ) : (
        <div className="mt-8" data-testid="trace-done">
          <div className="flex justify-center gap-1">{[0, 1, 2].map((i) => <Star key={i} size={30} className={i < feedbackForScore(finalScore).stars ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#E5DECF]"} />)}</div>
          <p className="mt-2 font-heading text-xl font-bold text-[#1F2937]">You wrote it! {finalScore}%</p>
          <button onClick={() => onComplete(finalScore)} data-testid="trace-continue" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md hover:scale-105"><CheckCircle2 size={18} /> Next <ArrowRight size={18} /></button>
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, primary, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-heading font-bold transition-transform active:scale-95 disabled:opacity-40 ${primary ? "bg-[#0D9488] text-white hover:bg-[#0B7F74]" : "border-2 border-[#E5DECF] bg-white text-[#1F2937] hover:bg-[#F2ECE1]"}`}>
      {children}
    </button>
  );
}
